import React, { FC, useEffect, useState } from "react"
import {
    Text,
    View,
    StyleSheet,
    TouchableOpacity,
    ActivityIndicator
} from "react-native"

import { AuthContext } from '../../contexts/auth/Store';
import { GetOrderGroupOrderItems } from '../../constants/Utils';

import { AnalyticsService } from "../../constants/Analytics";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { IOrderGroup, IOrderGroupItem } from "../../interfaces/order";
import Table from "../common/Table";
import StyledButton from "../StyledButton";
import StyledIcon from "../StyledIcon";
import { useNavigation } from "@react-navigation/native";
import OrderService from "../../services/OrderService";
import { OrderContext } from "../../contexts/order/Store";
import { formatCurrency } from "../../utils/money";

const analytics = new AnalyticsService();
const orderService = new OrderService();

interface IProps {
    orderGroup: IOrderGroup
}

type TableRow = (string | React.ReactElement)[]

interface ITable {
    tableHead: TableRow,
    tableData: TableRow[],
}

const DummyRow: FC<{ count: number }> = ({ count = 3 }) => {
    let rows = []
    for (let index = 0; index < count + 1; index++) {
        rows.push(index);
    }
    return <>
        {
            rows.map(value => <View key={value} style={styles.dummyRow}>
                <View style={[styles.dummyCell, { flex: 1 }]}></View>
                <View style={[styles.dummyCell, { flex: 7 }]}></View>
                <View style={[styles.dummyCell, { flex: 3 }]}></View>
                <View style={[styles.dummyCell, { flex: 3 }]}></View>
                <View style={[styles.dummyCell, { flex: 3 }]}></View>
                <View style={[styles.dummyCell, { flex: 4 }]}></View>
            </View>)
        }
    </>
}

const InvoiceTable: FC<IProps> = ({ orderGroup }) => {
    const navigation = useNavigation();

    const auth = React.useContext(AuthContext)
    const buyer = auth.getUserData.buyerData
    const { action: order_fetch } = React.useContext(OrderContext);

    const [loading, setLoading]: [boolean, any] = useState(false)
    const [error, setError]: [any, any] = useState(null)
    const [editing, setEditing]: [number | null, any] = useState(null)

    const [table, setTable]: [ITable, any] = useState({
        tableHead: ['Item', 'Cases', 'Units', `Rate`, `Amount`],
        tableData: []
    })

    const dropOrderGroupItem = (rowIndex: number, orderGroupItem: IOrderGroupItem) => {
        setEditing(rowIndex)
        orderService
            .removeItem({
                _id: orderGroupItem?._id
            })
            .then((data: any) => {
                //
                analytics.logEvent('Cancelled Order', buyer, { order_item: orderGroupItem })
                order_fetch()
                    .then(() => null)
                    .catch(() => null)

                let _array = []
                setTable((prev: ITable) => {
                    _array = Array.from(prev.tableData).filter((row, index) => index !== rowIndex)
                    return {
                        ...prev,
                        tableData: _array
                    }
                })
                if (!_array.length) {
                    navigation.navigate('OrderList')
                }
                setEditing(null)
            })
            .catch(err => {
                setEditing(null)
            })
    }

    const formatTableData = (orderItems: IOrderGroupItem[]): TableRow[] => {
        return orderItems.map((orderItem, index) => {
            return [
                // `${index + 1}`,
                <OrderItemView text={`${orderItem?.product?.name}`} index={index} orderGroupItem={orderItem} />,
                `${Math.floor(orderItem?.quantity / orderItem?.product?.case_count)}`,
                `${orderItem?.quantity % orderItem?.product?.case_count}`,
                formatCurrency(
                    orderItem.amount / (orderItem.quantity / orderItem.product?.case_count), true, 0),
                formatCurrency(orderItem.amount, true, 0)
            ]
        })
    }

    const fetchOrderItems = () => {
        // fetch order group items
        setLoading(true)
        if (orderGroup && !loading && !error) {
            setTable((prev: ITable) => {
                return {
                    ...prev,
                    tableData: GetOrderGroupOrderItems(orderGroup)
                }
            })
            setLoading(false)
            setError(false)
        } else {
            setLoading(false)
            setError(true)
        }
    }

    const editable = (unix_time: number) => {
        let expiry_time = (unix_time * 1000) + (3 * 60 * 60 * 1000) // added 3hrs (in milliseconds)
        let now = new Date().getTime()
        return now < expiry_time
    }

    const OrderItemView: FC<{ text: string, index: number, orderGroupItem: IOrderGroupItem }> = ({ text, index, orderGroupItem }) => {

        // prop these when extracting component
        // dropOrderGroupItem
        // editing
        // editable

        return <View
            style={{
                flexDirection: 'row',
            }}
        >
            {
                editable(orderGroupItem?.created)
                    ? editing === index
                        ? <View>
                            <ActivityIndicator
                                color={Colors.borderColor}
                                size={16}
                            />
                        </View>
                        : <TouchableOpacity
                            onPress={() => dropOrderGroupItem(index, orderGroupItem)}
                            disabled={editing !== null}
                            style={{ opacity: editing !== null ? 0.3 : 1 }}
                        >
                            <StyledIcon
                                iconName="md-trash"
                                iconColor={Colors.dangerText}
                                size={16}
                                iconOnly
                            />
                        </TouchableOpacity>
                    : null
            }
            <Text
                textBreakStrategy='highQuality'
                style={[
                    styles._tbody,
                    {
                        overflow: 'hidden',
                        flexShrink: 1,
                        paddingLeft: 4
                    }
                ]}
            >{text}</Text>
        </View>
    }

    useEffect(() => {
        fetchOrderItems()
        return () => {
            // clean up
        };
    }, []);

    return <>
        {
            loading
                ? <View style={styles.dummyContainer}>
                    <DummyRow count={GetOrderGroupOrderItems(orderGroup).length} />
                </View>
                : error
                    ? <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>Unable to get invoice details.</Text>
                        <StyledButton
                            label={`Retry`}
                            action={fetchOrderItems}
                            size='sm'
                            style={styles.errorButton}
                        />
                    </View>
                    : <View>
                        <Table
                            headerArr={table.tableHead}
                            bodyArr={formatTableData(table.tableData)}
                            flexArr={[8, 3, 3, 3, 4]}
                            numericIndices={[1, 2, 3, 4]}

                            tableStyle={styles._table}
                            bodyRowStyle={styles._row}
                            bodyCellTextStyle={styles._tbody}
                        />
                    </View>
        }
    </>
}

const styles = StyleSheet.create({
    _table: {
        backgroundColor: Colors.whiteColor,
        marginTop: 6,
        flexShrink: 0,
        paddingHorizontal: 10,
    },
    _row: {
    },
    _theader: {
        ...FontStyles.bold,
        ...FontStyles.size12,
        ...FontStyles.lh16,
    },
    _tbody: {
        ...FontStyles.dark,
        ...FontStyles.normal,
        ...FontStyles.size12,
        ...FontStyles.lh14
    },

    dummyContainer: {
        backgroundColor: Colors.whiteColor,
        marginTop: 6,
    },
    dummyRow: {
        flexDirection: 'row',
        borderBottomColor: Colors.borderColor,
        borderBottomWidth: 1,
        borderStyle: 'solid'
    },
    dummyCell: {
        backgroundColor: Colors.borderColor,
        borderRadius: 4,
        minHeight: 16,
        margin: 6
    },

    errorContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 30,
        marginTop: 6,
        backgroundColor: Colors.whiteColor,
    },
    errorText: {
        ...FontStyles.normal,
        ...FontStyles.size14,
        ...FontStyles.lh18,
    },
    errorButton: {
        height: 36,
        marginTop: 20
    }
})

export default InvoiceTable